import React, { useState, useRef, useEffect, useCallback } from 'react';
import { setContextSuppressed, getContextChipInfo, getSelectionChipInfo, clearAssistantSelection } from '../../utils/assistantContext';

/**
 * AssistantSidebar — the inline, full-height left panel, now STRICTLY the
 * Context-Aware AI chat. Tools and Tasks moved out to the right-side
 * UtilitySidebar (opened from the main nav).
 *
 * Header:  [history icon] Context-Aware AI ................... [+ New]
 * Below header: lock-on chip (the page the AI is analyzing).
 * Body:    the chat surface (portaled in by AssistantLauncher).
 * Bottom:  composer.
 *
 * The main-nav assistant icon toggles this panel (and shows an X while
 * open), so the sidebar itself no longer needs a close button — New lives
 * where the X used to be.
 */

const ICON = {
  history: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>),
  send: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5M5 12l7-7 7 7" /></svg>),
  plus: (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>),
};

export default function AssistantSidebar({ width, onClose, onTaskCountChange }) {
  const [input, setInput] = useState('');
  const textareaRef = useRef(null);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`;
  }, [input]);

  useEffect(() => {
    const handler = () => onTaskCountChange?.();
    window.addEventListener('banyak:tasks_updated', handler);
    return () => window.removeEventListener('banyak:tasks_updated', handler);
  }, [onTaskCountChange]);

  // Lock-on: track the current page context for the chip.
  const [lockCtx, setLockCtx] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    const current = getContextChipInfo();
    if (current) setLockCtx(current);
    const handler = (e) => {
      const d = e?.detail || {};
      setLockCtx(d.present ? d : null);
      setUnlocked(false);
    };
    window.addEventListener('banyak:assistant_context', handler);
    return () => window.removeEventListener('banyak:assistant_context', handler);
  }, []);
  const locked = lockCtx && !unlocked;

  // Spatial selection (point-and-click context).
  const [selection, setSelection] = useState(null);
  useEffect(() => {
    const cur = getSelectionChipInfo();
    if (cur) setSelection(cur);
    const handler = (e) => setSelection(e?.detail?.present ? e.detail : null);
    window.addEventListener('banyak:assistant_selection', handler);
    return () => window.removeEventListener('banyak:assistant_selection', handler);
  }, []);
  const startSelectMode = () => window.dispatchEvent(new CustomEvent('banyak:assistant_select_mode'));

  // The chat slot is always present now (single tab), so tell the launcher
  // to (re)find it on mount.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('banyak:assistant_chat_slot'));
  }, []);

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    window.dispatchEvent(new CustomEvent('banyak:assistant_open_with_text', { detail: { text } }));
    setInput('');
  }, [input]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  return (
    <div className="h-full w-full flex flex-col cpp-assistant-bg">
      {/* Header: history (left) + title, + New (right) */}
      <div className="flex-shrink-0 h-11 px-2 border-b cpp-assistant-border flex items-center justify-between cpp-assistant-header">
        <div className="flex items-center gap-1.5 min-w-0">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('banyak:assistant_toggle_history'))}
            title="Conversation history"
            aria-label="Conversation history"
            className="w-7 h-7 rounded flex items-center justify-center text-amber-700/80 hover:text-amber-900 hover:bg-amber-100 transition-colors flex-shrink-0"
          >
            {ICON.history}
          </button>
          <span className="text-sm font-semibold text-amber-900 truncate">Context-Aware AI</span>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('banyak:assistant_new_chat'))}
          title="New conversation"
          className="h-7 px-2 rounded flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 transition-colors flex-shrink-0"
        >
          {ICON.plus}<span>New</span>
        </button>
      </div>

      {/* Lock-on chip — where the old New/history toolbar used to be. */}
      {lockCtx && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-1.5 border-b cpp-assistant-border bg-amber-50/60">
          <div className="flex items-center gap-1.5 min-w-0 text-[11px]">
            <svg className={`w-3.5 h-3.5 flex-shrink-0 ${locked ? 'text-amber-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={locked ? 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z' : 'M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z'} />
            </svg>
            <span className={`truncate ${locked ? 'text-amber-800 font-medium' : 'text-gray-500'}`}>
              {locked ? `${lockCtx.label} · ${lockCtx.count} shown` : 'Unlocked'}
            </span>
          </div>
          <button
            onClick={() => setUnlocked(u => { const nv = !u; setContextSuppressed(nv); return nv; })}
            className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded text-amber-700 hover:bg-amber-100 transition-colors"
            title={locked ? 'Stop analyzing this page' : 'Analyze this page'}
          >
            {locked ? 'Unlock' : 'Lock on'}
          </button>
        </div>
      )}

      {/* Selection chip — a more precise, point-and-click context that sits
          alongside the full-page lock. */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2 px-3 py-1.5 border-b cpp-assistant-border">
        {selection ? (
          <>
            <div className="flex items-center gap-1.5 min-w-0 text-[11px]">
              <svg className="w-3.5 h-3.5 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <span className="truncate text-emerald-800 font-medium">
                {selection.label} · {selection.count} {selection.kind === 'text' ? 'block' + (selection.count === 1 ? '' : 's') : 'item' + (selection.count === 1 ? '' : 's')}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={startSelectMode} className="text-[10px] font-medium px-1.5 py-0.5 rounded text-emerald-700 hover:bg-emerald-50 transition-colors" title="Reselect">Reselect</button>
              <button onClick={() => { clearAssistantSelection(); }} className="text-[10px] font-medium px-1.5 py-0.5 rounded text-gray-500 hover:bg-gray-100 transition-colors" title="Clear selection">Clear</button>
            </div>
          </>
        ) : (
          <button
            onClick={startSelectMode}
            className="flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
            title="Click or drag to select part of the page as context"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
            Select on page
          </button>
        )}
      </div>

      {/* Chat surface (portaled in by AssistantLauncher) */}
      <div id="assistant-chat-slot" className="flex-1 min-h-0 relative" />

      {/* Composer */}
      <div className="flex-shrink-0 border-t cpp-assistant-border p-2">
        <div className="flex items-end gap-2 bg-white border border-amber-200 rounded-lg px-2 py-1.5 focus-within:border-amber-400 focus-within:ring-1 focus-within:ring-amber-400 transition-colors">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask assistant…"
            className="flex-1 bg-transparent outline-none text-sm text-gray-900 placeholder-gray-400 resize-none leading-5 max-h-[140px]"
          />
          <button
            onClick={submit}
            disabled={!input.trim()}
            aria-label="Send"
            className={`flex-shrink-0 w-8 h-8 rounded flex items-center justify-center transition-colors ${
              input.trim() ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {ICON.send}
          </button>
        </div>
      </div>
    </div>
  );
}
